import { RouterProvider, createBrowserRouter, Navigate } from 'react-router-dom';
import { Suspense, useEffect } from 'react';
import './utils/fetchInterceptor'; // Global fetch interceptor
import { useSearchParams } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { useWindowFocus } from './hooks/useWindowFocus';
import { useProactiveServices } from './hooks/useProactiveServices';
import { useBrowserEvents } from './hooks/useBrowserEvents';
import { useClimateScenarios } from './hooks/useClimateScenarios';
import { useCategoryMetricsUpdater } from './hooks/useCategoryMetricsUpdater';

import PerformanceDashboard from './components/PerformanceDashboard';
import PerformanceAlerts from './components/PerformanceAlerts';
import AdvancedRecoveryDashboard from './components/AdvancedRecoveryDashboard';
import { useAuth } from './contexts/AuthContext';
import GlobalErrorBoundary from './components/GlobalErrorBoundary';
import TempPasswordWarningModal from './components/TempPasswordWarningModal';
import ChunkErrorBoundary from './components/ChunkErrorBoundary';
import ErrorRecoveryModal from './components/ErrorRecoveryModal';
import { useErrorRecovery } from './hooks/useErrorRecovery';
import { autoCleanWrongFirmaCache } from './utils/clearWrongFirmaCache';
import UniversalPreviewModal from './components/UniversalPreviewModal';
import { lazyWithRetry as lazy } from './utils/runtimeStability';

// Kök, Layout ve Koruma Bileşenleri (Hemen yükle)
import Root from './Root';
import ProtectedRoute from './components/routing/ProtectedRoute';
import ProtectedAdminRoute from './components/routing/ProtectedAdminRoute';
import AppLayout from './components/AppLayout';
import AdminLayout from './admin/layouts/AdminLayout';
import AuthLayout from './layouts/AuthLayout';

// Loading Component
const PageLoader = () => (
  <div className="flex items-center justify-center min-h-screen">
    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
  </div>
);

// Critical Pages (No lazy loading)
import LoginPage from './pages/LoginPage';
const FirstLoginPage = lazy(() => import('./pages/FirstLoginPage'));

const Anasayfa = lazy(() => import('./pages/Anasayfa'));
const KonuSecimi = lazy(() => import('./pages/KonuSecimi').catch(() => import('./pages/KonuSecimi')));
const VeriToplama = lazy(() => import('./pages/VeriToplama').catch(() => ({ 
  default: () => (
    <div style={{padding:'20px',textAlign:'center'}}>
      <h3>Veri Toplama sayfası yüklenemedi</h3>
      <button onClick={()=>window.location.reload()} style={{padding:'10px 20px',background:'#007bff',color:'white',border:'none',borderRadius:'4px',cursor:'pointer'}}>
        🔄 Sayfayı Yenile
      </button>
    </div>
  ) 
})));
const VeriToplamaIndex = lazy(() => import('./pages/VeriToplamaIndex').catch(() => import('./pages/VeriToplamaIndex')));
const AnketHazirla = lazy(() => import('./pages/AnketHazirla').catch(() => import('./pages/AnketHazirla')));
const CifteOnemlilikMatrisi = lazy(() => import('./pages/CifteOnemlilikMatrisi').catch(() => import('./pages/CifteOnemlilikMatrisi')));
const TSRS = lazy(() => import('./pages/TSRS').catch(() => ({ default: () => <div>TSRS sayfası yüklenemedi. Sayfayı yenileyin.</div> })));
const TSRSErrorBoundary = lazy(() => import('./components/TSRSErrorBoundary'));
const TSRSSeraGaziEmisynlari = lazy(() => import('./pages/TSRSSeraGaziEmisynlari').catch(() => ({ default: () => <div>Sera Gazı Emisyonları sayfası yüklenemedi. Sayfayı yenileyin.</div> })));
const TSRSSeraGaziHedefTablosu = lazy(() => import('./pages/TSRSSeraGaziHedefTablosu').catch(() => ({ default: () => <div>Sera Gazı Hedef Tablosu sayfası yüklenemedi. Sayfayı yenileyin.</div> })));
const SeraGaziHesaplamalari = lazy(() => import('./pages/SeraGaziHesaplamalari').catch(() => ({ default: () => <div>Sera Gazı Hesaplamaları sayfası yüklenemedi. Sayfayı yenileyin.</div> })));
const TSRSSuVerileri = lazy(() => import('./pages/TSRSSuVerileri').catch(() => ({ default: () => <div>Su Verileri sayfası yüklenemedi. Sayfayı yenileyin.</div> })));
const TSRSSuperAdminAnalysis = lazy(() => import('./pages/TSRSSuperAdminAnalysis').catch(() => ({ default: () => <div>TSRS Süper Admin Analiz sayfası yüklenemedi. Sayfayı yenileyin.</div> })));
const IsGucuVerileri = lazy(() => import('./pages/IsGucuVerileri').catch(() => import('./pages/IsGucuVerileri')));
const IsgVerileri = lazy(() => import('./pages/IsgVerileri').catch(() => import('./pages/IsgVerileri')));
const UpdatePasswordPage = lazy(() => import('./pages/UpdatePasswordPage'));
const ProfilimPage = lazy(() => import('./pages/ProfilimPage'));
const Riskler = lazy(() => import('./pages/Riskler').catch(() => ({ default: () => <div style={{padding:'20px',textAlign:'center'}}><button onClick={()=>window.location.reload()}>Riskler sayfası yüklenemedi. Sayfayı yenileyin.</button></div> })));
const RiskForm = lazy(() => import('./pages/RiskForm'));
const Firsatlar = lazy(() => import('./pages/Firsatlar'));
const Hedefler = lazy(() => import('./pages/Hedefler'));
const HedefForm = lazy(() => import('./pages/HedefForm'));
const DegerZinciri = lazy(() => import('./pages/DegerZinciri').catch(() => ({ default: () => <div style={{padding:'20px',textAlign:'center'}}><button onClick={()=>window.location.reload()}>Değer Zinciri sayfası yüklenemedi. Sayfayı yenileyin.</button></div> })));
const Raporlama = lazy(() => import('./pages/Raporlama'));
const PerformansGostergeleri = lazy(() => import('./pages/PerformansGostergeleri'));
const PerformansBolumAtama = lazy(() => import('./pages/PerformansBolumAtama'));
const SecurityDocsPage = lazy(() => import('./pages/SecurityDocsPage'));
const KVKKAcikRizaPage = lazy(() => import('./pages/KVKKAcikRizaPage'));
const KVKKAydinlatmaPage = lazy(() => import('./pages/KVKKAydinlatmaPage'));
const BilgiBankasi = lazy(() => import('./pages/BilgiBankasi'));
const KapsananIsletmeler = lazy(() => import('./pages/tsrs/KapsananIsletmeler').catch(() => ({ default: () => <div>Kapsanan İşletmeler sayfası yüklenemedi. Sayfayı yenileyin.</div> })));
const BeceriYetkinlikMatrisi = lazy(() => import('./pages/BeceriYetkinlikMatrisi'));
const GRIGenel = lazy(() => import('./pages/GRIGenel'));
const GRIAnaliz = lazy(() => import('./pages/GRIAnaliz'));
const GRIEvrensel = lazy(() => import('./pages/GRIEvrensel'));
const GRIEvrenselDinamik = lazy(() => import('./pages/GRIEvrenselDinamik'));
const GRIEkonomik = lazy(() => import('./pages/GRIEkonomik'));
const GRICevresel = lazy(() => import('./pages/GRICevresel'));
const GRISosyal = lazy(() => import('./pages/GRISosyal'));
const GRISuperAdminAnalysis = lazy(() => import('./pages/GRISuperAdminAnalysis').catch(() => ({ default: () => <div>GRI Süper Admin Analiz sayfası yüklenemedi. Sayfayı yenileyin.</div> })));
const VeriToplamaSuperAdminAnalysis = lazy(() => import('./pages/VeriToplamaSuperAdminAnalysis').catch(() => ({ default: () => <div>Veri Toplama Süper Admin Analiz sayfası yüklenemedi. Sayfayı yenileyin.</div> })));
const AIChat = lazy(() => import('./components/AIChat'));
const PreviewTest = lazy(() => import('./pages/PreviewTest'));
const GlassmorphismTest = lazy(() => import('./pages/GlassmorphismTest'));
const ScenarioAnalysis = lazy(() => import('./features/scenario-analysis/ScenarioAnalysisPage'));
const SenaryoAnalizleriRapor = lazy(() => import('./pages/SenaryoAnalizleriRapor'));
const IklimRiskleri = lazy(() => import('./pages/IklimRiskleri'));
const KarbonAyakIzi = lazy(() => import('./pages/KarbonAyakIzi'));
const UyumStratejileri = lazy(() => import('./pages/UyumStratejileri'));

// Yeni Senaryo Modülleri
const SenaryoListesiPage = lazy(() => import('./pages/SenaryoListesiPage'));
const YeniSenaryoFormPage = lazy(() => import('./pages/YeniSenaryoFormPage'));
const SenaryoDetayPage = lazy(() => import('./pages/SenaryoDetayPage'));
const ScenarioAnalysisV2Page = lazy(() => import('./pages/ScenarioAnalysisV2Page'));
const ScenarioDetailV2Page = lazy(() => import('./pages/ScenarioDetailV2Page'));
const ScenarioAnalysisV3Page = lazy(() => import('./features/scenario-analysis-v3/ScenarioAnalysisV3Page'));
const PortfolioCockpitPage = lazy(() => import('./pages/PortfolioCockpitPage'));
const StressLabPage = lazy(() => import('./pages/StressLabPage'));

// SAP Entegrasyon Sayfaları
const KarbonAyakIziDashboard = lazy(() => import('./pages/KarbonAyakIziDashboard'));
const TedarikciDegerlendirme = lazy(() => import('./pages/TedarikciDegerlendirme'));
const SAPSettings = lazy(() => import('./pages/admin/SAPSettings'));
const SAPSyncLogs = lazy(() => import('./pages/admin/SAPSyncLogs'));
const CompliancePage = lazy(() => import('./pages/CompliancePage'));

// Lazy Load Admin Pages
const AdminDashboardPage = lazy(() => import('./admin/pages/AdminDashboardPage'));
const UserListPage = lazy(() => import('./admin/pages/UserManagement/UserListPage'));
const NewUserPage = lazy(() => import('./pages/admin/NewUserPage'));
const UserEditPage = lazy(() => import('./admin/pages/UserManagement/UserEditPage'));
const DemoRequestsPage = lazy(() => import('./admin/pages/DemoRequestsPage'));
const CompanyListPage = lazy(() => import('./admin/pages/CompanyManagement/CompanyListPage'));
const DepartmentListPage = lazy(() => import('./admin/pages/DepartmentManagement/DepartmentListPage'));
import KonuAtamaPage from './pages/KonuAtamaPage';
const CompanyManagementPage = lazy(() => import('./admin/pages/CompanyManagement/CompanyManagementPage'));
const RAGManagement = lazy(() => import('./pages/admin/RAGManagement'));
const PDFAnalyticsPage = lazy(() => import('./pages/admin/PDFAnalyticsPage'));
const ReportAnalysis = lazy(() => import('./pages/ReportAnalysis'));
const CacheManagement = lazy(() => import('./pages/admin/CacheManagement'));
const LearningManagement = lazy(() => import('./pages/admin/LearningManagement'));
const EmbeddingManagement = lazy(() => import('./pages/admin/EmbeddingManagement'));
const ActivityLogsPage = lazy(() => import('./admin/pages/ActivityManagement/ActivityLogsPage'));
const PageAnalyticsPage = lazy(() => import('./admin/pages/ActivityManagement/PageAnalyticsPage'));
const SystemAnalyticsPage = lazy(() => import('./admin/pages/ActivityManagement/SystemAnalyticsPage'));
const ActivityAnalyticsPage = lazy(() => import('./admin/pages/ActivityManagement/ActivityAnalyticsPage'));
const RealtimeActivityMonitorPage = lazy(() => import('./admin/pages/ActivityManagement/RealtimeActivityMonitorPage'));
const NotificationCenterPage = lazy(() => import('./admin/pages/NotificationManagement/NotificationCenterPage'));
const NotificationHistoryPage = lazy(() => import('./admin/pages/NotificationManagement/NotificationHistoryPage'));
const DashboardAnalyticsPage = lazy(() => import('./admin/pages/Analytics/DashboardAnalyticsPage'));
const DetailedReportsPage = lazy(() => import('./admin/pages/Analytics/DetailedReportsPage'));
const SecuritySettingsPage = lazy(() => import('./admin/pages/SystemManagement/SecuritySettingsPage'));
const SystemHealthPage = lazy(() => import('./admin/pages/SystemManagement/SystemHealthPage'));
const LearningAnalyticsPage = lazy(() => import('./admin/pages/SystemManagement/LearningAnalyticsPage'));
const BackupManagementPage = lazy(() => import('./admin/pages/SystemManagement/BackupManagementPage'));
const MessagesPage = lazy(() => import('./admin/pages/MessagesPage'));
const MessageTemplatesPage = lazy(() => import('./admin/pages/MessageTemplatesPage'));
const SuperAdminNotificationsPage = lazy(() => import('./admin/pages/SuperAdminNotificationsPage'));
const RoleManagementDashboard = lazy(() => import('./admin/pages/RoleManagement/RoleManagementDashboard'));
const PermissionMatrixPage = lazy(() => import('./admin/pages/RoleManagement/PermissionMatrixPage'));
const RoleDefinitionsPage = lazy(() => import('./admin/pages/RoleManagement/RoleDefinitionsPage'));
const RoleAuditPage = lazy(() => import('./admin/pages/RoleManagement/RoleAuditPage'));
const RollbackSystemPage = lazy(() => import('./admin/pages/RoleManagement/RollbackSystemPage'));
const SmartSuggestionsPage = lazy(() => import('./admin/pages/RoleManagement/SmartSuggestionsPage'));
const ReportingDashboard = lazy(() => import('./admin/pages/RoleManagement/ReportingDashboard'));



// Auth Callback Component
const AuthCallback = () => {
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const hashParams = new URLSearchParams(window.location.hash.substring(1));
    
    // Tüm parametreleri first-login'e aktar
    const allParams = new URLSearchParams();
    params.forEach((value, key) => allParams.set(key, value));
    hashParams.forEach((value, key) => allParams.set(key, value));
    
    window.location.href = `/auth/first-login?${allParams.toString()}`;
  }, []);
  
  return (
    <div style={{ padding: '2rem', textAlign: 'center' }}>
      <h2>Yönlendiriliyor...</h2>
      <p>Lütfen bekleyin...</p>
    </div>
  );
};

// Token Redirect Handler Component
const TokenRedirectHandler = () => {
  const [searchParams] = useSearchParams();
  const accessToken = searchParams.get('access_token');
  const refreshToken = searchParams.get('refresh_token');
  const type = searchParams.get('type');
  const tokenHash = searchParams.get('token_hash');
  const error = searchParams.get('error');
  
  // Hash'teki token'ları da kontrol et
  let hashAccessToken, hashRefreshToken, hashType, hashError;
  if (window.location.hash) {
    const hashParams = new URLSearchParams(window.location.hash.substring(1));
    hashAccessToken = hashParams.get('access_token');
    hashRefreshToken = hashParams.get('refresh_token');
    hashType = hashParams.get('type');
    hashError = hashParams.get('error');
  }
  
  // Herhangi bir auth token varsa first-login'e yönlendir
  if (tokenHash || accessToken || hashAccessToken || type || hashType || error || hashError) {
    const allParams = new URLSearchParams();
    
    // Search params'tan ekle
    searchParams.forEach((value, key) => {
      allParams.set(key, value);
    });
    
    // Hash params'tan ekle
    if (window.location.hash) {
      const hashParams = new URLSearchParams(window.location.hash.substring(1));
      hashParams.forEach((value, key) => {
        allParams.set(key, value);
      });
    }
    
    return <Navigate to={`/auth/first-login?${allParams.toString()}`} replace />;
  }
  
  // Hiçbir auth data yoksa normal login'e yönlendir
  return <Navigate to="/auth/login" replace />;
};

const router = createBrowserRouter([
  // Ana domain'i static redirect sayfasına yönlendir
  {
    path: "/",
    element: <Navigate to="/auth/login" replace />
  },
  {
    element: <Root />, // 1. seviye: Context'leri sağlar
    children: [
      {
        path: "/auth",
        element: <AuthLayout />,
        children: [
          { path: "login", element: <LoginPage /> },
          { path: "first-login", element: <Suspense fallback={<PageLoader />}><FirstLoginPage /></Suspense> },
          { path: "callback", element: <AuthCallback /> },

          { path: "update-password", element: <Suspense fallback={<PageLoader />}><UpdatePasswordPage /></Suspense> },
          { path: "kvkk-acik-riza", element: <Suspense fallback={<PageLoader />}><KVKKAcikRizaPage /></Suspense> },
          { path: "kvkk-aydinlatma", element: <Suspense fallback={<PageLoader />}><KVKKAydinlatmaPage /></Suspense> }
        ]
      },
      { path: "/login", element: <Navigate to="/auth/login" replace /> },
      // Admin Paneli Rotaları
      {
        path: "/admin",
        element: <ProtectedAdminRoute />, // 2. seviye: Admin yetkisini kontrol eder
        children: [
          {
            element: <AdminLayout />, // 3. seviye: Yetki varsa Admin Çerçevesini (sol menü vb.) render eder
            children: [
                // 4. seviye: Çerçevenin içine gelecek olan asıl sayfalar
                { index: true, element: <Navigate to="dashboard" replace /> },
                { path: "dashboard", element: <Suspense fallback={<PageLoader />}><AdminDashboardPage /></Suspense> },
                { path: "users", element: <Suspense fallback={<PageLoader />}><UserListPage /></Suspense> },
                { path: "users/new", element: <Suspense fallback={<PageLoader />}><NewUserPage /></Suspense> },
                { path: "users/edit/:userId", element: <Suspense fallback={<PageLoader />}><UserEditPage /></Suspense> },
                { path: "demo-requests", element: <Suspense fallback={<PageLoader />}><DemoRequestsPage /></Suspense> },
                { path: "companies", element: <Suspense fallback={<PageLoader />}><CompanyListPage /></Suspense> },
                { path: "company-management", element: <Suspense fallback={<PageLoader />}><CompanyManagementPage /></Suspense> },
                { path: "departments", element: <Suspense fallback={<PageLoader />}><DepartmentListPage /></Suspense> },
                { path: "rag-management", element: <Suspense fallback={<PageLoader />}><RAGManagement /></Suspense> },
                { path: "pdf-analytics", element: <Suspense fallback={<PageLoader />}><PDFAnalyticsPage /></Suspense> },
              { path: "cache-management", element: <Suspense fallback={<PageLoader />}><CacheManagement /></Suspense> },
              { path: "learning-management", element: <Suspense fallback={<PageLoader />}><LearningManagement /></Suspense> },
              { path: "embedding-management", element: <Suspense fallback={<PageLoader />}><EmbeddingManagement /></Suspense> },
              { path: "activity-logs", element: <Suspense fallback={<PageLoader />}><ActivityLogsPage /></Suspense> },
              { path: "page-analytics", element: <Suspense fallback={<PageLoader />}><PageAnalyticsPage /></Suspense> },
              { path: "system-analytics", element: <Suspense fallback={<PageLoader />}><SystemAnalyticsPage /></Suspense> },
              { path: "activity-analytics", element: <Suspense fallback={<PageLoader />}><ActivityAnalyticsPage /></Suspense> },
              { path: "realtime-monitor", element: <Suspense fallback={<PageLoader />}><RealtimeActivityMonitorPage /></Suspense> },
              { path: "notification-center", element: <Suspense fallback={<PageLoader />}><NotificationCenterPage /></Suspense> },
              { path: "notification-history", element: <Suspense fallback={<PageLoader />}><NotificationHistoryPage /></Suspense> },
              { path: "dashboard-analytics", element: <Suspense fallback={<PageLoader />}><DashboardAnalyticsPage /></Suspense> },
              { path: "detailed-reports", element: <Suspense fallback={<PageLoader />}><DetailedReportsPage /></Suspense> },
              { path: "security-settings", element: <Suspense fallback={<PageLoader />}><SecuritySettingsPage /></Suspense> },
              { path: "system-health", element: <Suspense fallback={<PageLoader />}><SystemHealthPage /></Suspense> },
              { path: "learning-analytics", element: <Suspense fallback={<PageLoader />}><LearningAnalyticsPage /></Suspense> },
              { path: "backup-management", element: <Suspense fallback={<PageLoader />}><BackupManagementPage /></Suspense> },
              { path: "messages", element: <Suspense fallback={<PageLoader />}><MessagesPage /></Suspense> },
              { path: "message-templates", element: <Suspense fallback={<PageLoader />}><MessageTemplatesPage /></Suspense> },
              { path: "system-notifications", element: <Suspense fallback={<PageLoader />}><SuperAdminNotificationsPage /></Suspense> },
              { path: "role-management", element: <Suspense fallback={<PageLoader />}><RoleManagementDashboard /></Suspense> },
              { path: "permission-matrix", element: <Suspense fallback={<PageLoader />}><PermissionMatrixPage /></Suspense> },
              { path: "role-definitions", element: <Suspense fallback={<PageLoader />}><RoleDefinitionsPage /></Suspense> },
              { path: "role-audit", element: <Suspense fallback={<PageLoader />}><RoleAuditPage /></Suspense> },
              { path: "role-rollback", element: <Suspense fallback={<PageLoader />}><RollbackSystemPage /></Suspense> },
              { path: "role-suggestions", element: <Suspense fallback={<PageLoader />}><SmartSuggestionsPage /></Suspense> },
              { path: "role-reports", element: <Suspense fallback={<PageLoader />}><ReportingDashboard /></Suspense> },
              { path: "sap-settings", element: <Suspense fallback={<PageLoader />}><SAPSettings /></Suspense> },
              { path: "sap-sync-logs", element: <Suspense fallback={<PageLoader />}><SAPSyncLogs /></Suspense> },
                
            ]
          }
        ]
      },

      // Genel Uygulama Rotaları
      {
        path: "/",
        element: <ProtectedRoute />, // Sadece login kontrolü
        children: [
          // Glassmorphism Test Page - NO LAYOUT (to show gradient background)
          { path: "glassmorphism-test", element: <Suspense fallback={<PageLoader />}><GlassmorphismTest /></Suspense> },
          {
            element: <AppLayout />,
            children: [
              { path: "anasayfa", element: <Suspense fallback={<PageLoader />}><Anasayfa /></Suspense> },
              { path: "onemli-konu-secimi", element: <Suspense fallback={<PageLoader />}><KonuSecimi /></Suspense> },
              { path: "veri-toplama-sureci", element: <ChunkErrorBoundary><Suspense fallback={<PageLoader />}><VeriToplama /></Suspense></ChunkErrorBoundary> },
              { path: "veri-toplama-sureci/index", element: <Suspense fallback={<PageLoader />}><VeriToplamaIndex /></Suspense> },
              { path: "veri-toplama-super-admin-analysis", element: <Suspense fallback={<PageLoader />}><VeriToplamaSuperAdminAnalysis /></Suspense> },
              { path: "anket-hazirla", element: <ChunkErrorBoundary><Suspense fallback={<PageLoader />}><AnketHazirla /></Suspense></ChunkErrorBoundary> },
              { path: "cifte-onemlilik-matrisi", element: <ChunkErrorBoundary><Suspense fallback={<PageLoader />}><CifteOnemlilikMatrisi /></Suspense></ChunkErrorBoundary> },
              { path: "tsrs", element: <ChunkErrorBoundary><Suspense fallback={<PageLoader />}><TSRSErrorBoundary><TSRS /></TSRSErrorBoundary></Suspense></ChunkErrorBoundary> },
              { path: "tsrs-super-admin-analysis", element: <ChunkErrorBoundary><Suspense fallback={<PageLoader />}><TSRSSuperAdminAnalysis /></Suspense></ChunkErrorBoundary> },
              { path: "tsrs-sera-gazi-emisyonlari", element: <ChunkErrorBoundary><Suspense fallback={<PageLoader />}><TSRSSeraGaziEmisynlari /></Suspense></ChunkErrorBoundary> },
              { path: "tsrs-sera-gazi-hedef-tablosu", element: <ChunkErrorBoundary><Suspense fallback={<PageLoader />}><TSRSSeraGaziHedefTablosu /></Suspense></ChunkErrorBoundary> },
              { path: "sera-gazi-hesaplamalari", element: <ChunkErrorBoundary><Suspense fallback={<PageLoader />}><SeraGaziHesaplamalari /></Suspense></ChunkErrorBoundary> },
              { path: "tsrs-su-verileri", element: <ChunkErrorBoundary><Suspense fallback={<PageLoader />}><TSRSSuVerileri /></Suspense></ChunkErrorBoundary> },
              { path: "isgucuverileri", element: <ChunkErrorBoundary><Suspense fallback={<PageLoader />}><IsGucuVerileri /></Suspense></ChunkErrorBoundary> },
              { path: "isg-verileri", element: <ChunkErrorBoundary><Suspense fallback={<PageLoader />}><IsgVerileri /></Suspense></ChunkErrorBoundary> },
              { path: "profilim", element: <Suspense fallback={<PageLoader />}><ProfilimPage /></Suspense> },
              { path: "konu-atama", element: <KonuAtamaPage /> },
              { path: "riskler", element: <ChunkErrorBoundary><Suspense fallback={<PageLoader />}><Riskler /></Suspense></ChunkErrorBoundary> },
              { path: "riskler/yeni", element: <Suspense fallback={<PageLoader />}><RiskForm /></Suspense> },
              { path: "riskler/:id/duzenle", element: <Suspense fallback={<PageLoader />}><RiskForm /></Suspense> },
              { path: "firsatlar", element: <ChunkErrorBoundary><Suspense fallback={<PageLoader />}><Firsatlar /></Suspense></ChunkErrorBoundary> },
              { path: "hedefler", element: <ChunkErrorBoundary><Suspense fallback={<PageLoader />}><Hedefler /></Suspense></ChunkErrorBoundary> },
              { path: "hedefler/yeni", element: <ChunkErrorBoundary><Suspense fallback={<PageLoader />}><HedefForm /></Suspense></ChunkErrorBoundary> },
              { path: "hedefler/:id/duzenle", element: <ChunkErrorBoundary><Suspense fallback={<PageLoader />}><HedefForm isEdit={true} /></Suspense></ChunkErrorBoundary> },
              { path: "deger-zinciri", element: <ChunkErrorBoundary><Suspense fallback={<PageLoader />}><DegerZinciri /></Suspense></ChunkErrorBoundary> },
              { path: "raporlama", element: <Suspense fallback={<PageLoader />}><Raporlama /></Suspense> },
              { path: "performans-bolum-ata", element: <Suspense fallback={<PageLoader />}><PerformansBolumAtama /></Suspense> },
              { path: "performans-gostergeleri", element: <ChunkErrorBoundary><Suspense fallback={<PageLoader />}><PerformansGostergeleri /></Suspense></ChunkErrorBoundary> },
              { path: "guvenlik-dokumanlari", element: <Suspense fallback={<PageLoader />}><SecurityDocsPage /></Suspense> },
              { path: "report-analysis", element: <Suspense fallback={<PageLoader />}><ReportAnalysis /></Suspense> },
              { path: "bilgi-bankasi", element: <Suspense fallback={<PageLoader />}><BilgiBankasi /></Suspense> },
              { path: "kapsanan-isletmeler", element: <Suspense fallback={<PageLoader />}><KapsananIsletmeler /></Suspense> },
              { path: "beceri-yetkinlik-matrisi", element: <Suspense fallback={<PageLoader />}><BeceriYetkinlikMatrisi /></Suspense> },
              { path: "gri-genel", element: <ChunkErrorBoundary><Suspense fallback={<PageLoader />}><GRIGenel /></Suspense></ChunkErrorBoundary> },
              { path: "gri-analiz", element: <ChunkErrorBoundary><Suspense fallback={<PageLoader />}><GRIAnaliz /></Suspense></ChunkErrorBoundary> },
              { path: "gri-evrensel", element: <ChunkErrorBoundary><Suspense fallback={<PageLoader />}><GRIEvrensel /></Suspense></ChunkErrorBoundary> },
              { path: "gri-evrensel/:moduleId", element: <ChunkErrorBoundary><Suspense fallback={<PageLoader />}><GRIEvrenselDinamik /></Suspense></ChunkErrorBoundary> },
              { path: "gri-ekonomik", element: <ChunkErrorBoundary><Suspense fallback={<PageLoader />}><GRIEkonomik /></Suspense></ChunkErrorBoundary> },
              { path: "gri-cevresel", element: <ChunkErrorBoundary><Suspense fallback={<PageLoader />}><GRICevresel /></Suspense></ChunkErrorBoundary> },
              { path: "gri-sosyal", element: <ChunkErrorBoundary><Suspense fallback={<PageLoader />}><GRISosyal /></Suspense></ChunkErrorBoundary> },
              { path: "gri-super-admin-analysis", element: <ChunkErrorBoundary><Suspense fallback={<PageLoader />}><GRISuperAdminAnalysis /></Suspense></ChunkErrorBoundary> },
              { path: "ai-chat", element: <Suspense fallback={<PageLoader />}><AIChat /></Suspense> },
              { path: "preview-test", element: <Suspense fallback={<PageLoader />}><PreviewTest /></Suspense> },
              // YENİ SENARYO SİSTEMİ
              { path: "senaryo-analizi", element: <Suspense fallback={<PageLoader />}><SenaryoListesiPage /></Suspense> },
              { path: "senaryo-analizi/yeni", element: <Suspense fallback={<PageLoader />}><YeniSenaryoFormPage /></Suspense> },
              { path: "senaryo-analizi/:senaryoId", element: <Suspense fallback={<PageLoader />}><SenaryoDetayPage /></Suspense> },
              { path: "senaryo-analizi-v2", element: <Suspense fallback={<PageLoader />}><ScenarioAnalysisV2Page /></Suspense> },
              { path: "senaryo-analizi-v2/:id", element: <Suspense fallback={<PageLoader />}><ScenarioDetailV2Page /></Suspense> },
              { path: "senaryo-analizi-v3", element: <Suspense fallback={<PageLoader />}><ScenarioAnalysisV3Page /></Suspense> },
              { path: "senaryo-analizi-v3/yeni", element: <Suspense fallback={<PageLoader />}><ScenarioAnalysisV3Page /></Suspense> },
              { path: "senaryo-analizi-v3/:id", element: <Suspense fallback={<PageLoader />}><ScenarioAnalysisV3Page /></Suspense> },
              { path: "senaryo-analizleri-rapor", element: <Suspense fallback={<PageLoader />}><SenaryoAnalizleriRapor /></Suspense> },
              { path: "iklim-riskleri", element: <Suspense fallback={<PageLoader />}><IklimRiskleri /></Suspense> },
              { path: "karbon-ayak-izi", element: <Suspense fallback={<PageLoader />}><KarbonAyakIzi /></Suspense> },
              { path: "uyum-stratejileri", element: <Suspense fallback={<PageLoader />}><UyumStratejileri /></Suspense> },
              { path: "portfolio-cockpit", element: <Suspense fallback={<PageLoader />}><PortfolioCockpitPage /></Suspense> },
              { path: "stress-lab", element: <Suspense fallback={<PageLoader />}><StressLabPage /></Suspense> },
              { path: "karbon-ayak-izi-dashboard", element: <Suspense fallback={<PageLoader />}><KarbonAyakIziDashboard /></Suspense> },
              { path: "tedarikci-degerlendirme", element: <Suspense fallback={<PageLoader />}><TedarikciDegerlendirme /></Suspense> },
              { path: "compliance", element: <Suspense fallback={<PageLoader />}><CompliancePage /></Suspense> }
            ]
          }
        ]
      },
      {
        path: "*",
        element: <Navigate to="/auth/login" replace />,
      }
    ]
  }
]);

function AppContent() {
  const { isModalOpen, currentError, recoveryActions, closeModal } = useErrorRecovery();
  const isAuthRoute = window.location.pathname.startsWith('/auth');
  const shouldRunAppServices = !isAuthRoute;
  
  // Proaktif servisleri başlat
  const { trackAction, reportError, getSystemHealth } = useProactiveServices(shouldRunAppServices);
  
  // Browser event'lerini dinle
  useBrowserEvents(shouldRunAppServices);
  
  // İklim senaryolarını otomatik güncelle
  useClimateScenarios(shouldRunAppServices);
  
  // Kategori metriklerini periyodik güncelle
  useCategoryMetricsUpdater(shouldRunAppServices);
  
  // Yanlış firma cache'lerini otomatik temizle
  useEffect(() => {
    autoCleanWrongFirmaCache();
  }, []);
  
  return (
    <>
      <RouterProvider router={router} />
      <Toaster 
        position="top-right" 
        toastOptions={{
          style: {
            zIndex: 99999
          }
        }}
      />
      <PerformanceAlerts />
      <ConditionalPerformanceDashboard />
      <AdvancedRecoveryDashboard />
      <ErrorRecoveryModal
        isOpen={isModalOpen}
        onClose={closeModal}
        error={currentError}
        recoveryActions={recoveryActions}
      />
      <UniversalPreviewModal />
    </>
  );
}

// AuthProvider içinde useAuth kullanabilen component
function ConditionalPerformanceDashboard() {
  try {
    const { profile } = useAuth();
    const isSuperAdmin = profile?.rol === 'ekostrateji_super_admin';
    return isSuperAdmin ? <PerformanceDashboard /> : null;
  } catch (error) {
    // AuthProvider yoksa hiçbir şey render etme
    return null;
  }
}



function App() {
  const isWindowFocused = useWindowFocus();
  
  return (
    <GlobalErrorBoundary>
      <AppContent />
    </GlobalErrorBoundary>
  );
}

export default App;
